import React, { useState, useMemo, useCallback } from "react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ComposedChart, Line, Cell, ReferenceLine,
} from "recharts";
import moment from "moment";
import { Columns2, Rows2, TriangleAlert } from "lucide-react";
import { getAccountCurrency, displayDecimalFormatted } from "../../../utils/systemSettings";
import { getCookie, setCookie } from "../../../helpers/cookieUtils";
import styles from "../ResourcesReport.module.scss";

const WORK_DAYS_COOKIE = "resources_report_work_days";

const COLORS = {
  // Hours chart – paired: alloc vs logged
  capacity: "#342c82",       // deep indigo – baseline
  allocation: "#7c6dd8",     // lighter purple – planned hours
  logged: "#a78bfa",         // lavender – actual hours (compare with allocation)
  utilization: "#f59e0b",    // amber – planned util %
  loggedUtilization: "#f97316", // orange – actual util % (compare with utilization)
  // Cost & Revenue chart – paired
  cost: "#e05c5c",           // red – planned cost
  loggedCost: "#f87171",     // lighter red – actual cost (compare with cost)
  bill: "#0ea5e9",           // blue – planned bill
  loggedBill: "#38bdf8",     // lighter blue – actual bill (compare with bill)
  costVariance: "#fb923c",   // orange – cost delta
  billVariance: "#818cf8",   // indigo – bill delta
  allocGp: "#10b981",        // emerald – planned GP
  loggedGp: "#34d399",       // lighter emerald – actual GP (compare with allocGp)
};

function getHoursAlerts(d, fmtHrs) {
  const alerts = [];
  const logged = d.logged ?? 0;
  const allocation = d.allocation ?? 0;
  if (logged > allocation) {
    alerts.push(`Hours overrun: +${fmtHrs ? fmtHrs(logged - allocation) : Number((logged - allocation).toFixed(2)) + "h"}`);
  }
  return alerts;
}

function getFinancialAlerts(d, fmtCurr) {
  const alerts = [];
  const lCost = d.loggedCost ?? 0;
  const aCost = d.allocationCost ?? 0;
  if (lCost > aCost) {
    alerts.push(`Cost overrun: +${fmtCurr ? fmtCurr(lCost - aCost) : (lCost - aCost).toFixed(2)}`);
  }
  const lBill = d.loggedBill ?? 0;
  const aBill = d.allocationBill ?? 0;
  if (lBill < aBill) {
    alerts.push(`Bill shortfall: -${fmtCurr ? fmtCurr(aBill - lBill) : (aBill - lBill).toFixed(2)}`);
  }
  return alerts;
}

const CustomTooltip = ({ active, payload, label, currency, settings, alertType }) => {
  if (!active || !payload?.length) return null;
  const dataPoint = payload[0]?.payload;
  const fmtCurr = (val) => `${currency} ${displayDecimalFormatted(settings, val ?? 0)}`;
  const fmtHrs = (val) => `${displayDecimalFormatted(settings, Number((val ?? 0).toFixed(2)))}h`;
  const overruns = dataPoint
    ? alertType === "hours" ? getHoursAlerts(dataPoint, fmtHrs) : getFinancialAlerts(dataPoint, fmtCurr)
    : [];
  return (
    <div className={styles.chartTooltip}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{dataPoint?.tooltipLabel || label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} style={{ display: "flex", alignItems: "center", gap: 6, padding: "1px 0" }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: entry.color, flexShrink: 0 }} />
          <span style={{ color: "#5e5e5e" }}>{entry.name}:</span>
          <span style={{ fontWeight: 500 }}>
            {entry.dataKey.includes("Cost") || entry.dataKey.includes("Bill") || entry.dataKey.includes("costVariance") || entry.dataKey.includes("billVariance") || entry.dataKey.includes("Gp")
              ? fmtCurr(entry.value)
              : entry.dataKey === "utilization" || entry.dataKey === "loggedUtilization"
              ? `${displayDecimalFormatted(settings, Number((entry.value ?? 0).toFixed(2)))}%`
              : `${displayDecimalFormatted(settings, Number((entry.value ?? 0).toFixed(2)))}h`}
          </span>
        </div>
      ))}
      {overruns.length > 0 && (
        <div style={{ borderTop: "1px solid #fee2e2", marginTop: 6, paddingTop: 4 }}>
          {overruns.map((msg, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 500 }}>
              <TriangleAlert size={12} color="#dc2626" /> {msg}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const OverrunTick = ({ x, y, payload, data, alertType }) => {
  const d = data?.find((item) => item.label === payload?.value);
  const hasOverrun = d
    ? (alertType === "hours" ? getHoursAlerts(d, null) : getFinancialAlerts(d, null)).length > 0
    : false;
  const isWeekend = d && d.isWorkingDay === false;
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={14} textAnchor="middle" fontSize={11} fill={isWeekend ? "#c4c4c4" : "#666"} fontStyle={isWeekend ? "italic" : "normal"}>
        {payload?.value}
      </text>
      {hasOverrun && (
        <g transform="translate(-5, 20)">
          <TriangleAlert size={12} color="#dc2626" />
        </g>
      )}
    </g>
  );
};

const GRANULARITY_OPTIONS = [
  { value: "day", label: "Daily" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
  { value: "quarter", label: "Quarterly" },
];

// Width per data point by granularity
const WIDTH_PER_POINT = { day: 40, week: 60, month: 100, quarter: 120 };

function mapDay(d) {
  const m = moment(d.date);
  return {
    date: d.date,
    isWorkingDay: d.isWorkingDay,
    label: m.format("dd D"),
    tooltipLabel: m.format("ddd, MMM D YYYY"),
    capacity: d.capacityHours ?? 0,
    allocation: d.allocationHours ?? 0,
    logged: d.loggedHours ?? 0,
    utilization: (d.capacityHours > 0 ? (d.allocationHours / d.capacityHours) * 100 : 0),
    allocationCost: d.allocationCost ?? 0,
    allocationBill: d.allocationBill ?? 0,
    loggedCost: d.loggedCost ?? 0,
    loggedBill: d.loggedBill ?? 0,
    costVariance: (d.loggedCost ?? 0) - (d.allocationCost ?? 0),
    billVariance: (d.loggedBill ?? 0) - (d.allocationBill ?? 0),
    loggedUtilization: (d.capacityHours > 0 ? ((d.loggedHours ?? 0) / d.capacityHours) * 100 : 0),
    allocGp: (d.allocationBill ?? 0) - (d.allocationCost ?? 0),
    loggedGp: (d.loggedBill ?? 0) - (d.loggedCost ?? 0),
  };
}

function aggregateBucket(entries, label, tooltipLabel) {
  const capacity = entries.reduce((s, e) => s + e.capacity, 0);
  const allocation = entries.reduce((s, e) => s + e.allocation, 0);
  const logged = entries.reduce((s, e) => s + e.logged, 0);
  const allocationCost = entries.reduce((s, e) => s + e.allocationCost, 0);
  const allocationBill = entries.reduce((s, e) => s + e.allocationBill, 0);
  const loggedCost = entries.reduce((s, e) => s + e.loggedCost, 0);
  const loggedBill = entries.reduce((s, e) => s + e.loggedBill, 0);
  return {
    label,
    tooltipLabel,
    capacity,
    allocation,
    logged,
    utilization: capacity > 0 ? (allocation / capacity) * 100 : 0,
    allocationCost,
    allocationBill,
    loggedCost,
    loggedBill,
    costVariance: loggedCost - allocationCost,
    billVariance: loggedBill - allocationBill,
    loggedUtilization: capacity > 0 ? (logged / capacity) * 100 : 0,
    allocGp: allocationBill - allocationCost,
    loggedGp: loggedBill - loggedCost,
  };
}

function aggregateWeekly(days) {
  const buckets = {};
  days.forEach((d) => {
    const m = moment(d.date);
    const key = `${m.isoWeekYear()}-W${String(m.isoWeek()).padStart(2, "0")}`;
    if (!buckets[key]) buckets[key] = { entries: [], start: m.clone(), end: m.clone() };
    buckets[key].entries.push(d);
    if (m.isBefore(buckets[key].start)) buckets[key].start = m.clone();
    if (m.isAfter(buckets[key].end)) buckets[key].end = m.clone();
  });
  return Object.entries(buckets).map(([, b]) =>
    aggregateBucket(b.entries, `W${moment(b.start).isoWeek()}`, `${b.start.format("MMM D")} – ${b.end.format("MMM D")}`)
  );
}

function aggregateMonthly(days) {
  const buckets = {};
  days.forEach((d) => {
    const key = d.date.substring(0, 7);
    if (!buckets[key]) buckets[key] = [];
    buckets[key].push(d);
  });
  return Object.entries(buckets).map(([key, entries]) => {
    const label = moment(key, "YYYY-MM").format("MMM YYYY");
    return aggregateBucket(entries, label, label);
  });
}

function aggregateQuarterly(days) {
  const buckets = {};
  days.forEach((d) => {
    const m = moment(d.date);
    const q = m.quarter();
    const key = `${m.year()}-Q${q}`;
    if (!buckets[key]) buckets[key] = [];
    buckets[key].push(d);
  });
  return Object.entries(buckets).map(([key, entries]) =>
    aggregateBucket(entries, key.split("-")[1] + " " + key.split("-")[0], key.replace("-", " "))
  );
}

export default function ReportCharts({ dailyBreakdown, settings, columns }) {
  const currency = getAccountCurrency(settings);
  const [hiddenHours, setHiddenHours] = useState({});
  const [hiddenFinancial, setHiddenFinancial] = useState({});
  const [granularity, setGranularity] = useState("week");
  const [chartLayout, setChartLayout] = useState("inline");
  const [workingDaysOnly, setWorkingDaysOnly] = useState(() => {
    const saved = getCookie(WORK_DAYS_COOKIE);
    return saved !== null ? saved === "true" : true;
  });

  const toggleWorkingDays = useCallback(() => {
    setWorkingDaysOnly((prev) => {
      const next = !prev;
      setCookie(WORK_DAYS_COOKIE, String(next));
      return next;
    });
  }, []);

  const visibleKeys = new Set(columns.filter((col) => col.isVisible).map((col) => col.key));

  const colLabel = (colKey, fallback) => {
    const col = columns.find((c) => c.key === colKey);
    return col?.customName || fallback;
  };

  const dailyData = useMemo(() => (dailyBreakdown || []).map(mapDay), [dailyBreakdown]);

  const filteredDays = useMemo(
    () => (workingDaysOnly ? dailyData.filter((d) => d.isWorkingDay) : dailyData),
    [dailyData, workingDaysOnly]
  );

  const chartData = useMemo(() => {
    switch (granularity) {
      case "day": return filteredDays;
      case "week": return aggregateWeekly(filteredDays);
      case "month": return aggregateMonthly(filteredDays);
      case "quarter": return aggregateQuarterly(filteredDays);
      default: return filteredDays;
    }
  }, [granularity, filteredDays]);

  const minWidthPerPoint = WIDTH_PER_POINT[granularity] || 60;
  const dataWidth = chartData.length * minWidthPerPoint;
  const Y_AXIS_WIDTH = 85;
  const Y_AXIS_WIDTH_Hours = 80;
  const isDaily = granularity === "day";

  const FINANCIAL_KEYS = ["allocationCost", "allocationBill", "loggedCost", "loggedBill", "costVariance", "billVariance", "allocGp", "loggedGp"];
  const hasPositiveFinancial = chartData.some((d) => FINANCIAL_KEYS.some((k) => (d[k] ?? 0) > 0));
  const financialDomain = hasPositiveFinancial
    ? [(min) => Math.min(0, min), (max) => Math.max(0, max)]
    : ["auto", 0];

  // Render Cell children for weekend dimming in daily view
  const weekendCells = (color) =>
    isDaily
      ? chartData.map((d, i) => (
          <Cell key={i} fill={color} opacity={d.isWorkingDay === false ? 0.3 : 1} />
        ))
      : null;

  return (
    <div className={styles.chartsSection}>
      <div className={styles.chartsHeader}>
        <div className={styles.granularityToggle}>
          <button
            type="button"
            className={`${styles.granularityBtn} ${chartLayout === "inline" ? styles.granularityBtnActive : ""}`}
            onClick={() => setChartLayout("inline")}
            title="Side by side"
          >
            <Columns2 size={14} />
          </button>
          <button
            type="button"
            className={`${styles.granularityBtn} ${chartLayout === "stacked" ? styles.granularityBtnActive : ""}`}
            onClick={() => setChartLayout("stacked")}
            title="One per row"
          >
            <Rows2 size={14} />
          </button>
        </div>
        <div className={styles.granularityToggle}>
          {GRANULARITY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`${styles.granularityBtn} ${granularity === opt.value ? styles.granularityBtnActive : ""}`}
              onClick={() => setGranularity(opt.value)}
            >
              {opt.label}
            </button>
          ))}
          <button
            type="button"
            className={`${styles.granularityBtn} ${workingDaysOnly ? styles.granularityBtnActive : ""}`}
            onClick={toggleWorkingDays}
            title="Working days only"
          >
            Work days
          </button>
        </div>
      </div>

      <div className={`${styles.chartsGrid} ${chartLayout === "stacked" ? styles.chartsGridStacked : ""}`}>
        {/* Chart 1: Hours & Utilization */}
        <div className={styles.chartCard}>
          <div className={styles.chartTitleRow}>
            <h4 className={styles.chartTitle}>Hours &amp; Utilization</h4>
            <div className={styles.chartLegend}>
              {[
                { key: "capacity", label: colLabel("capacityHours", "Capacity"), color: COLORS.capacity, visKey: "capacityHours" },
                { key: "allocation", label: colLabel("allocationHours", "Allocation"), color: COLORS.allocation, visKey: "allocationHours" },
                { key: "logged", label: colLabel("loggedHours", "Logged"), color: COLORS.logged, visKey: "loggedHours" },
                { key: "utilization", label: colLabel("utilization", "Utilization"), color: COLORS.utilization, visKey: "utilization" },
                { key: "loggedUtilization", label: colLabel("loggedUtilization", "Logged Util"), color: COLORS.loggedUtilization, visKey: "loggedUtilization" },
              ].filter((item) => visibleKeys.has(item.visKey)).map((item) => (
                <span
                  key={item.key}
                  className={styles.chartLegendItem}
                  style={{ opacity: hiddenHours[item.key] ? 0.35 : 1 }}
                  onClick={() => setHiddenHours((prev) => ({ ...prev, [item.key]: !prev[item.key] }))}
                >
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: item.color }} />
                  {item.label}
                </span>
              ))}
            </div>
          </div>
          <div className={styles.chartSplitContainer}>
            {/* Fixed Y-axis */}
            <div className={styles.chartYAxisFixed} style={{ width: Y_AXIS_WIDTH_Hours }}>
              <ResponsiveContainer width={Y_AXIS_WIDTH_Hours} height={280}>
                <ComposedChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 30 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'transparent' }} tickLine={false} axisLine={false} interval={0} />
                  <YAxis yAxisId="hours" tickFormatter={(value) => `${displayDecimalFormatted(settings, Number((value ?? 0)))}h`}  tick={{ fontSize: 11 }} width={Y_AXIS_WIDTH - 10} domain={[0, 'dataMax']} allowDecimals={false} allowDataOverflow={false} />
                  <YAxis yAxisId="pct" orientation="right" tick={{ fontSize: 0 }} width={0} domain={[0, 100]} allowDecimals={false} />
                  <Bar yAxisId="hours" dataKey="capacity" fill="transparent" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            {/* Scrollable chart area */}
            <div className={styles.chartScrollWrapper}>
              <div style={{ minWidth: dataWidth }}>
                <ResponsiveContainer width="100%" height={280}>
                  <ComposedChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={<OverrunTick data={chartData} alertType="hours" />} interval={0} />
                    <YAxis yAxisId="hours" tickFormatter={(value) => `${displayDecimalFormatted(settings, Number((value ?? 0)))}h`} hide domain={[0, 'dataMax']} allowDecimals={false} allowDataOverflow={false} />
                    <YAxis yAxisId="pct" orientation="right" tick={{ fontSize: 11 }} domain={[0, 100]} unit="%" allowDecimals={false} />
                    <Tooltip content={<CustomTooltip currency={currency} settings={settings} alertType="hours" />} />
                    {visibleKeys.has("capacityHours") && !hiddenHours.capacity && (
                      <Bar yAxisId="hours" dataKey="capacity" name={colLabel("capacityHours", "Capacity")} fill={COLORS.capacity} radius={[4, 4, 0, 0]}>{weekendCells(COLORS.capacity)}</Bar>
                    )}
                    {visibleKeys.has("allocationHours") && !hiddenHours.allocation && (
                      <Bar yAxisId="hours" dataKey="allocation" name={colLabel("allocationHours", "Allocation")} fill={COLORS.allocation} radius={[4, 4, 0, 0]}>{weekendCells(COLORS.allocation)}</Bar>
                    )}
                    {visibleKeys.has("loggedHours") && !hiddenHours.logged && (
                      <Bar yAxisId="hours" dataKey="logged" name={colLabel("loggedHours", "Logged")} fill={COLORS.logged} radius={[4, 4, 0, 0]}>{weekendCells(COLORS.logged)}</Bar>
                    )}
                    {visibleKeys.has("utilization") && !hiddenHours.utilization && (
                      <Line yAxisId="pct" dataKey="utilization" name={colLabel("utilization", "Utilization")} stroke={COLORS.utilization} strokeWidth={2} dot={{ r: 3 }} />
                    )}
                    {visibleKeys.has("loggedUtilization") && !hiddenHours.loggedUtilization && (
                      <Line yAxisId="pct" dataKey="loggedUtilization" name={colLabel("loggedUtilization", "Logged Util")} stroke={COLORS.loggedUtilization} strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 3" />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>

        {/* Chart 2: Cost & Revenue */}
        <div className={styles.chartCard}>
          <div className={styles.chartTitleRow}>
            <h4 className={styles.chartTitle}>Cost &amp; Revenue</h4>
            <div className={styles.chartLegend}>
              {[
                { key: "allocationCost", label: colLabel("allocCost", "Alloc Cost"), color: COLORS.cost, visKey: "allocCost" },
                { key: "allocationBill", label: colLabel("allocBill", "Alloc Bill"), color: COLORS.bill, visKey: "allocBill" },
                { key: "loggedCost", label: colLabel("loggedCost", "Logged Cost"), color: COLORS.loggedCost, visKey: "loggedCost" },
                { key: "loggedBill", label: colLabel("loggedBill", "Logged Bill"), color: COLORS.loggedBill, visKey: "loggedBill" },
                { key: "allocGp", label: colLabel("allocGp", "Alloc GP"), color: COLORS.allocGp, visKey: "allocGp" },
                { key: "loggedGp", label: colLabel("loggedGp", "Logged GP"), color: COLORS.loggedGp, visKey: "loggedGp" },
                { key: "costVariance", label: colLabel("costVariance", "Cost Variance"), color: COLORS.costVariance, visKey: "costVariance" },
                { key: "billVariance", label: colLabel("billVariance", "Bill Variance"), color: COLORS.billVariance, visKey: "billVariance" },
              ].filter((item) => visibleKeys.has(item.visKey)).map((item) => (
                <span
                  key={item.key}
                  className={styles.chartLegendItem}
                  style={{ opacity: hiddenFinancial[item.key] ? 0.35 : 1 }}
                  onClick={() => setHiddenFinancial((prev) => ({ ...prev, [item.key]: !prev[item.key] }))}
                >
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: item.color }} />
                  {item.label}
                </span>
              ))}
            </div>
          </div>
          <div className={styles.chartSplitContainer}>
            {/* Fixed Y-axis */}
            <div className={styles.chartYAxisFixed} style={{ width: Y_AXIS_WIDTH }}>
              <ResponsiveContainer width={Y_AXIS_WIDTH} height={280}>
                <BarChart data={chartData} margin={{ top: 5, right: 0, left: 0, bottom: 30 }}>
                  <XAxis dataKey="label" tick={{ fontSize: 11, fill: 'transparent' }} tickLine={false} axisLine={false} interval={0} />
                  <YAxis tickFormatter={(value) => `${currency} ${displayDecimalFormatted(settings, Number((value ?? 0)))}`} tick={{ fontSize: 11 }} width={Y_AXIS_WIDTH - 10} domain={financialDomain} allowDecimals={false} />
                  <ReferenceLine y={0} stroke="#ccc" />
                  <Bar dataKey="allocationCost" fill="transparent" />
                  <Bar dataKey="costVariance" fill="transparent" />
                  <Bar dataKey="billVariance" fill="transparent" />
                  <Bar dataKey="allocGp" fill="transparent" />
                  <Bar dataKey="loggedGp" fill="transparent" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            {/* Scrollable chart area */}
            <div className={styles.chartScrollWrapper}>
              <div style={{ minWidth: dataWidth }}>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="label" tick={<OverrunTick data={chartData} alertType="financial" />} interval={0} />
                    <YAxis hide tickFormatter={(value) => `${currency} ${displayDecimalFormatted(settings, Number((value ?? 0)))}`} domain={financialDomain} allowDecimals={false} />
                    <ReferenceLine y={0} stroke="#ccc" />
                    <Tooltip content={<CustomTooltip currency={currency} settings={settings} alertType="financial" />} />
                    {visibleKeys.has("allocCost") && !hiddenFinancial.allocationCost && (
                      <Bar dataKey="allocationCost" name={colLabel("allocCost", "Alloc Cost")} fill={COLORS.cost} radius={[4, 4, 0, 0]}>{weekendCells(COLORS.cost)}</Bar>
                    )}
                    {visibleKeys.has("allocBill") && !hiddenFinancial.allocationBill && (
                      <Bar dataKey="allocationBill" name={colLabel("allocBill", "Alloc Bill")} fill={COLORS.bill} radius={[4, 4, 0, 0]}>{weekendCells(COLORS.bill)}</Bar>
                    )}
                    {visibleKeys.has("loggedCost") && !hiddenFinancial.loggedCost && (
                      <Bar dataKey="loggedCost" name={colLabel("loggedCost", "Logged Cost")} fill={COLORS.loggedCost} radius={[4, 4, 0, 0]}>{weekendCells(COLORS.loggedCost)}</Bar>
                    )}
                    {visibleKeys.has("loggedBill") && !hiddenFinancial.loggedBill && (
                      <Bar dataKey="loggedBill" name={colLabel("loggedBill", "Logged Bill")} fill={COLORS.loggedBill} radius={[4, 4, 0, 0]}>{weekendCells(COLORS.loggedBill)}</Bar>
                    )}
                    {visibleKeys.has("costVariance") && !hiddenFinancial.costVariance && (
                      <Bar dataKey="costVariance" name={colLabel("costVariance", "Cost Variance")} fill={COLORS.costVariance} radius={[4, 4, 0, 0]}>{weekendCells(COLORS.costVariance)}</Bar>
                    )}
                    {visibleKeys.has("billVariance") && !hiddenFinancial.billVariance && (
                      <Bar dataKey="billVariance" name={colLabel("billVariance", "Bill Variance")} fill={COLORS.billVariance} radius={[4, 4, 0, 0]}>{weekendCells(COLORS.billVariance)}</Bar>
                    )}
                    {visibleKeys.has("allocGp") && !hiddenFinancial.allocGp && (
                      <Bar dataKey="allocGp" name={colLabel("allocGp", "Alloc GP")} fill={COLORS.allocGp} radius={[4, 4, 0, 0]}>{weekendCells(COLORS.allocGp)}</Bar>
                    )}
                    {visibleKeys.has("loggedGp") && !hiddenFinancial.loggedGp && (
                      <Bar dataKey="loggedGp" name={colLabel("loggedGp", "Logged GP")} fill={COLORS.loggedGp} radius={[4, 4, 0, 0]}>{weekendCells(COLORS.loggedGp)}</Bar>
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
